import React, { useState, useEffect } from "react";
import { PageGuard } from "@/components/shared/PlanGuard";
import hosys from "@/api/api";
import { useVenue } from "@/components/shared/VenueContext";
import PageHeader from "@/components/shared/PageHeader";
import QuickLoader from "@/components/shared/QuickLoader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import ProcessingActivitiesList from "@/components/gdpr/ProcessingActivitiesList";
import ProcessingActivityForm from "@/components/gdpr/ProcessingActivityForm";
import ConsentsList from "@/components/gdpr/ConsentsList";
import ConsentForm from "@/components/gdpr/ConsentForm";
import DataIncidentsList from "@/components/gdpr/DataIncidentsList";
import DataIncidentForm from "@/components/gdpr/DataIncidentForm";

import {
  Shield, FileText, Users, AlertTriangle, Plus,
  CheckCircle2, Clock, XCircle, ShieldAlert, Bell,
} from "lucide-react";
import moment from "moment";
import "moment/locale/ca";
moment.locale("ca");

// RGPD document categories (uses existing Document entity)
const GDPR_DOC_CATEGORIES = ["protocol", "certificate", "other"];
const GDPR_DOC_TAGS = [
  "Política de privacitat",
  "Clàusula informativa clients",
  "Clàusula informativa treballadors",
  "Contracte amb encarregat (DPA)",
  "Registre d'activitats de tractament",
  "Procediment drets ARCO-POL",
  "Procediment bretxa de seguretat",
  "Anàlisi de riscos (EIPD)",
];

export default function GDPRManagement() {
  const { currentVenue, user, userRole, loading: venueLoading } = useVenue();
  const [activities, setActivities] = useState([]);
  const [consents, setConsents] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [gdprDocs, setGdprDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  // Forms
  const [showActivityForm, setShowActivityForm] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [showConsentForm, setShowConsentForm] = useState(false);
  const [editingConsent, setEditingConsent] = useState(null);
  const [showIncidentForm, setShowIncidentForm] = useState(false);
  const [editingIncident, setEditingIncident] = useState(null);

  const isAdmin = userRole === "superadmin" || userRole === "admin";

  useEffect(() => {
    if (currentVenue) loadAll();
  }, [currentVenue]);

  const loadAll = async () => {
    setLoading(true);
    const [acts, cons, incs, docs] = await Promise.all([
      hosys.entities.DataProcessingActivity.filter({ venue_id: currentVenue.id }, "-created_date"),
      hosys.entities.DataConsent.filter({ venue_id: currentVenue.id }, "-consent_date"),
      hosys.entities.DataIncident.filter({ venue_id: currentVenue.id }, "-discovery_date"),
      hosys.entities.Document.filter({ venue_id: currentVenue.id }, "-created_date"),
    ]);
    setActivities(acts);
    setConsents(cons);
    setIncidents(incs);
    // Filter GDPR-related documents by name keywords
    const rgpdDocs = docs.filter(d =>
      GDPR_DOC_TAGS.some(tag => d.name?.toLowerCase().includes(tag.toLowerCase().substring(0, 12)))
      || d.description?.toLowerCase().includes("rgpd")
      || d.description?.toLowerCase().includes("privacitat")
      || d.category === "protocol" || d.category === "haccp"
    );
    setGdprDocs(docs); // show all docs filterable
    setLoading(false);
  };

  if (venueLoading || loading) return <QuickLoader />;

  // Alerts calculation
  const openIncidents = incidents.filter(i => i.status !== "resolved");
  const criticalIncidents = incidents.filter(i => (i.risk_level === "high" || i.risk_level === "critical") && !i.notified_authority && i.status !== "resolved");
  const activeConsents = consents.filter(c => !c.withdrawn).length;
  const withdrawnConsents = consents.filter(c => c.withdrawn).length;
  const incompleteActivities = activities.filter(a => !a.retention_period || (a.data_categories || []).length === 0);

  const alerts = [];
  if (activities.length === 0) alerts.push({ type: "warning", msg: "No hi ha cap activitat de tractament registrada. El registre és obligatori (art. 30 RGPD)." });
  if (incompleteActivities.length > 0) alerts.push({ type: "warning", msg: `${incompleteActivities.length} activitat(s) incompletes (falten categories de dades o període de conservació).` });
  if (criticalIncidents.length > 0) alerts.push({ type: "critical", msg: `${criticalIncidents.length} incidència(ies) d'alt risc sense notificar a l'AEPD. Cal notificar en 72h!` });
  if (openIncidents.length > 0) alerts.push({ type: "danger", msg: `${openIncidents.length} incidència(ies) de dades obertes o en investigació.` });

  const content = (
    <div className="space-y-5">
      <PageHeader
        title="Protecció de Dades (RGPD)"
        subtitle={`${currentVenue?.name} · Gestió integral del compliment RGPD`}
      />

      {/* Alerts banner */}
      {alerts.length > 0 && (
        <div className="space-y-2">
          {alerts.map((a, i) => (
            <div key={`alert-${i}`} className={`rounded-2xl p-4 flex items-start gap-3 ${
              a.type === "critical" ? "bg-red-600 text-white" :
              a.type === "danger" ? "bg-red-50 border border-red-200 text-red-800" :
              "bg-amber-50 border border-amber-200 text-amber-800"
            }`}>
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <p className="text-sm font-medium">{a.msg}</p>
            </div>
          ))}
        </div>
      )}

      {/* KPI summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KpiCard icon={FileText} label="Activitats tractament" value={activities.length} color="text-blue-600" bg="bg-blue-50" iconColor="text-blue-500" onClick={() => setActiveTab("activities")} />
        <KpiCard icon={Users} label="Consentiments actius" value={activeConsents} color="text-emerald-600" bg="bg-emerald-50" iconColor="text-emerald-500" onClick={() => setActiveTab("consents")} />
        <KpiCard icon={AlertTriangle} label="Incidències obertes" value={openIncidents.length} color={openIncidents.length > 0 ? "text-red-600" : "text-emerald-600"} bg={openIncidents.length > 0 ? "bg-red-50" : "bg-emerald-50"} iconColor={openIncidents.length > 0 ? "text-red-500" : "text-emerald-500"} onClick={() => setActiveTab("incidents")} />
        <KpiCard icon={FileText} label="Documents RGPD" value={gdprDocs.length} color="text-purple-600" bg="bg-purple-50" iconColor="text-purple-500" onClick={() => setActiveTab("documents")} />
      </div>

      {/* Main tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-4 h-auto gap-1 p-1 bg-gray-100 rounded-xl">
          <TabsTrigger value="overview" className="rounded-lg text-xs py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm">
            Resum
          </TabsTrigger>
          <TabsTrigger value="activities" className="rounded-lg text-xs py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm">
            Registre tractament
            {activities.length > 0 && <span className="ml-1 text-[10px] bg-blue-100 text-blue-600 rounded-full px-1.5">{activities.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="consents" className="rounded-lg text-xs py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm">
            Consentiments
            {activeConsents > 0 && <span className="ml-1 text-[10px] bg-emerald-100 text-emerald-600 rounded-full px-1.5">{activeConsents}</span>}
          </TabsTrigger>
          <TabsTrigger value="incidents" className="rounded-lg text-xs py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm">
            Incidències
            {openIncidents.length > 0 && <span className="ml-1 text-[10px] bg-red-100 text-red-600 rounded-full px-1.5">{openIncidents.length}</span>}
          </TabsTrigger>
        </TabsList>

        {/* OVERVIEW */}
        <TabsContent value="overview" className="space-y-4 mt-4">
          {/* Quick access cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <OverviewSection
              icon={FileText} iconBg="bg-blue-50" iconColor="text-blue-600"
              title="Registre d'activitats" subtitle={`${activities.length} activitat(s) · ${incompleteActivities.length} incompleta(s)`}
              status={activities.length === 0 ? "empty" : incompleteActivities.length > 0 ? "warning" : "ok"}
              action={isAdmin && <Button size="sm" onClick={() => { setEditingActivity(null); setShowActivityForm(true); }} className="bg-[#7B2D3F] hover:bg-[#5B1D2F] h-8 gap-1 text-xs"><Plus className="w-3 h-3" /> Afegir</Button>}
              onClick={() => setActiveTab("activities")}
            >
              {activities.slice(0, 3).map(a => (
                <div key={a.id} className="text-xs text-gray-500 flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0" />
                  {a.name}
                </div>
              ))}
              {activities.length > 3 && <p className="text-xs text-gray-400">... i {activities.length - 3} més</p>}
            </OverviewSection>

            <OverviewSection
              icon={Users} iconBg="bg-emerald-50" iconColor="text-emerald-600"
              title="Control de consentiments" subtitle={`${activeConsents} actius · ${withdrawnConsents} retirats`}
              status={consents.length === 0 ? "empty" : "ok"}
              action={isAdmin && <Button size="sm" onClick={() => { setEditingConsent(null); setShowConsentForm(true); }} className="bg-[#7B2D3F] hover:bg-[#5B1D2F] h-8 gap-1 text-xs"><Plus className="w-3 h-3" /> Afegir</Button>}
              onClick={() => setActiveTab("consents")}
            >
              <div className="flex gap-3 text-xs">
                <span className="text-emerald-600 font-medium">{consents.filter(c => !c.withdrawn && c.subject_type === "client").length} clients</span>
                <span className="text-amber-600 font-medium">{consents.filter(c => !c.withdrawn && c.subject_type === "worker").length} treballadors</span>
              </div>
            </OverviewSection>

            <OverviewSection
              icon={ShieldAlert} iconBg="bg-red-50" iconColor="text-red-600"
              title="Incidències de dades" subtitle={`${openIncidents.length} obertes · ${incidents.filter(i => i.status === "resolved").length} resoltes`}
              status={criticalIncidents.length > 0 ? "critical" : openIncidents.length > 0 ? "warning" : incidents.length === 0 ? "empty" : "ok"}
              action={isAdmin && <Button size="sm" onClick={() => { setEditingIncident(null); setShowIncidentForm(true); }} className="bg-[#7B2D3F] hover:bg-[#5B1D2F] h-8 gap-1 text-xs"><Plus className="w-3 h-3" /> Registrar</Button>}
              onClick={() => setActiveTab("incidents")}
            >
              {openIncidents.slice(0, 2).map(inc => (
                <div key={inc.id} className="text-xs text-red-600 flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" />
                  {inc.title}
                </div>
              ))}
              {openIncidents.length === 0 && <p className="text-xs text-emerald-600">✓ Cap incidència oberta</p>}
            </OverviewSection>

            <OverviewSection
              icon={FileText} iconBg="bg-purple-50" iconColor="text-purple-600"
              title="Documents RGPD" subtitle={`${gdprDocs.length} documents al repositori`}
              status={gdprDocs.length === 0 ? "empty" : "ok"}
              action={null}
              onClick={() => setActiveTab("documents")}
            >
              {GDPR_DOC_TAGS.slice(0, 3).map(tag => {
                const hasDoc = gdprDocs.some(d => d.name?.toLowerCase().includes(tag.toLowerCase().substring(0, 12)));
                return (
                  <div key={tag} className="text-xs flex items-center gap-1.5">
                    {hasDoc
                      ? <CheckCircle2 className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                      : <Clock className="w-3 h-3 text-amber-400 flex-shrink-0" />
                    }
                    <span className={hasDoc ? "text-gray-600" : "text-gray-400"}>{tag}</span>
                  </div>
                );
              })}
            </OverviewSection>
          </div>
        </TabsContent>

        {/* ACTIVITIES */}
        <TabsContent value="activities" className="space-y-4 mt-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-gray-900">Registre d'Activitats de Tractament</h3>
              <p className="text-xs text-gray-400 mt-0.5">Obligatori per l'art. 30 RGPD · {activities.length} activitat(s)</p>
            </div>
            {isAdmin && (
              <Button onClick={() => { setEditingActivity(null); setShowActivityForm(true); }} className="bg-[#7B2D3F] hover:bg-[#5B1D2F] gap-2">
                <Plus className="w-4 h-4" /> Nova activitat
              </Button>
            )}
          </div>
          <ProcessingActivitiesList
            items={activities}
            onEdit={(item) => { setEditingActivity(item); setShowActivityForm(true); }}
            onDeleted={(id) => setActivities(prev => prev.filter(a => a.id !== id))}
            isAdmin={isAdmin}
          />
        </TabsContent>

        {/* CONSENTS */}
        <TabsContent value="consents" className="space-y-4 mt-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-gray-900">Control de Consentiments</h3>
              <p className="text-xs text-gray-400 mt-0.5">{activeConsents} actius · {withdrawnConsents} retirats</p>
            </div>
            {isAdmin && (
              <Button onClick={() => { setEditingConsent(null); setShowConsentForm(true); }} className="bg-[#7B2D3F] hover:bg-[#5B1D2F] gap-2">
                <Plus className="w-4 h-4" /> Registrar consentiment
              </Button>
            )}
          </div>
          <ConsentsList
            items={consents}
            onEdit={(item) => { setEditingConsent(item); setShowConsentForm(true); }}
            onDeleted={(id) => setConsents(prev => prev.filter(c => c.id !== id))}
            isAdmin={isAdmin}
          />
        </TabsContent>

        {/* INCIDENTS */}
        <TabsContent value="incidents" className="space-y-4 mt-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-gray-900">Incidències de Seguretat de Dades</h3>
              <p className="text-xs text-gray-400 mt-0.5">{openIncidents.length} obertes · {incidents.filter(i => i.status === "resolved").length} resoltes</p>
            </div>
            {isAdmin && (
              <Button onClick={() => { setEditingIncident(null); setShowIncidentForm(true); }} className="bg-[#7B2D3F] hover:bg-[#5B1D2F] gap-2">
                <Plus className="w-4 h-4" /> Registrar incidència
              </Button>
            )}
          </div>
          <DataIncidentsList
            items={incidents}
            onEdit={(item) => { setEditingIncident(item); setShowIncidentForm(true); }}
            onDeleted={(id) => setIncidents(prev => prev.filter(i => i.id !== id))}
            isAdmin={isAdmin}
          />
        </TabsContent>

        {/* DOCUMENTS */}
        <TabsContent value="documents" className="space-y-4 mt-4">
          <div>
            <h3 className="font-semibold text-gray-900">Documents RGPD</h3>
            <p className="text-xs text-gray-400 mt-0.5">Llista de comprovació de documents recomanats. Gestiona'ls des de la secció Documents.</p>
          </div>
          <div className="space-y-2">
            {GDPR_DOC_TAGS.map(tag => {
              const doc = gdprDocs.find(d => d.name?.toLowerCase().includes(tag.toLowerCase().substring(0, 12)));
              return (
                <Card key={tag} className={`p-4 border-0 shadow-sm flex items-center gap-3 ${doc ? "" : "border-l-4 border-l-amber-300 bg-amber-50/30"}`}>
                  <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${doc ? "bg-emerald-100" : "bg-amber-100"}`}>
                    {doc ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Clock className="w-4 h-4 text-amber-600" />}
                  </div>
                  <div className="flex-1">
                    <p className={`text-sm font-medium ${doc ? "text-gray-800" : "text-gray-600"}`}>{tag}</p>
                    {doc && <p className="text-xs text-gray-400">{doc.name} · {doc.uploaded_by_name}</p>}
                    {!doc && <p className="text-xs text-amber-600">Document no trobat al repositori</p>}
                  </div>
                  {doc?.file_url && (
                    <a href={doc.file_url} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" size="sm" className="h-7 text-xs gap-1">Obrir</Button>
                    </a>
                  )}
                </Card>
              );
            })}
          </div>
          <p className="text-xs text-gray-400 text-center">
            Per afegir documents, ves a la secció <strong>Documents</strong> del menú lateral.
          </p>
        </TabsContent>
      </Tabs>

      {/* Forms */}
      <ProcessingActivityForm
        open={showActivityForm}
        onClose={() => { setShowActivityForm(false); setEditingActivity(null); }}
        item={editingActivity}
        venueId={currentVenue.id}
        onSaved={loadAll}
      />
      <ConsentForm
        open={showConsentForm}
        onClose={() => { setShowConsentForm(false); setEditingConsent(null); }}
        item={editingConsent}
        venueId={currentVenue.id}
        onSaved={loadAll}
      />
      <DataIncidentForm
        open={showIncidentForm}
        onClose={() => { setShowIncidentForm(false); setEditingIncident(null); }}
        item={editingIncident}
        venueId={currentVenue.id}
        onSaved={loadAll}
      />
    </div>
  );
  return <PageGuard moduleCode="rgpd">{content}</PageGuard>;
}

function KpiCard({ icon: Icon, label, value, color, bg, iconColor, onClick }) {
  return (
    <Card className={`p-4 border-0 shadow-sm cursor-pointer hover:shadow-md transition-all ${bg}`} onClick={onClick}>
      <div className="flex items-center gap-3">
        <Icon className={`w-6 h-6 ${iconColor}`} />
        <div>
          <p className={`text-2xl font-bold ${color}`}>{value}</p>
          <p className="text-xs text-gray-500 leading-tight">{label}</p>
        </div>
      </div>
    </Card>
  );
}

function OverviewSection({ icon: Icon, iconBg, iconColor, title, subtitle, status, action, onClick, children }) {
  const statusBar = status === "critical" ? "border-l-red-600" : status === "warning" ? "border-l-amber-400" : status === "empty" ? "border-l-gray-200" : "border-l-emerald-400";
  return (
    <Card className={`p-4 border-0 shadow-sm border-l-4 ${statusBar} cursor-pointer hover:shadow-md transition-all`} onClick={onClick}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBg}`}>
            <Icon className={`w-4 h-4 ${iconColor}`} />
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 text-sm">{title}</h4>
            <p className="text-[11px] text-gray-400">{subtitle}</p>
          </div>
        </div>
        {action && <div onClick={e => e.stopPropagation()}>{action}</div>}
      </div>
      <div className="space-y-1">{children}</div>
    </Card>
  );
}